// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file RadioList.tsx
 * @input Uses React useId, useCallback, useRef, createContext, ReactNode, Field, InputStatus
 * @output Exports RadioList component, RadioListProps, RadioListContext
 * @position Core implementation; consumed by index.ts, tested by RadioList.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/RadioList/RadioList.doc.mjs
 * - /packages/core/src/RadioList/RadioList.test.tsx
 * - /packages/core/src/RadioList/index.ts
 * - /apps/storybook/stories/RadioList.stories.tsx
 * - /packages/cli/templates/blocks/components/RadioList/ (showcase blocks)
 */
import React, { createContext, useCallback, useId, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { Field } from "../Field/Field.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * Size of the radio controls, matching CheckboxInput sizes.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const RadioListContext = /*#__PURE__*/createContext(null);
RadioListContext.displayName = 'RadioListContext';
/**
 * A radio group component for single-value selection.
 *
 * @example
 * ```
 * <RadioList
 *   label="Notification preference"
 *   value={selected}
 *   onChange={setSelected}>
 *   <RadioListItem label="Email" value="email" />
 *   <RadioListItem label="SMS" value="sms" />
 *   <RadioListItem label="Push" value="push" />
 * </RadioList>
 * ```
 */
export function RadioList({
  ref,
  label,
  isLabelHidden = false,
  description,
  value,
  onChange,
  orientation = 'vertical',
  isDisabled = false,
  disabledMessage,
  isRequired = false,
  isOptional = false,
  size = 'md',
  status,
  labelTooltip,
  width,
  xstyle,
  className,
  style,
  'data-testid': dataTestId,
  htmlName,
  children
}) {
  const autoName = useId();
  const name = htmlName ?? autoName;
  const inputID = useId();
  const labelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const groupRef = useRef(null);

  // Disabled-reason tooltip. Applies to the whole-group disabled state. Disabled
  // controls swallow pointer events, so the tooltip listeners attach to the
  // radiogroup container and the radios stay perceivable via aria-disabled
  // instead of the disabled attribute. Selection is blocked in the item's
  // onChange guard.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The radiogroup container is not naturally focusable; focusin bubbles up
    // from the radios, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const contextValue = useMemo(() => ({
    name,
    value,
    onChange,
    isDisabled,
    hasDisabledMessage: showsDisabledMessage,
    isRequired,
    size,
    status
  }), [name, value, onChange, isDisabled, showsDisabledMessage, isRequired, size, status]);

  /**
   * Make the tab stop deterministic when a radio group has no selected value.
   *
   * Native `<input type="radio">` groups (same `name`) implement roving
   * tabindex for free: when a value is selected, that radio is the single tab
   * stop and receives focus, so no correction is needed there. But the ARIA
   * radio-group pattern (APG) also requires a deterministic tab stop when the
   * group has *no* selection — and browsers disagree here. Chrome, when
   * Shift+Tab moves focus backward into an unselected group, lands on the
   * *last* radio; forward Tab lands on the *first*. To keep the entry point
   * predictable we redirect focus:
   *   - forward entry  → first enabled radio (APG default)
   *   - backward entry → last enabled radio (matches Chrome's backward tab)
   *
   * The redirect only runs when focus arrives from *outside* the group's own
   * radios (guarded via `relatedTarget` containment). Moving between radios
   * inside the group — arrow keys, clicks, programmatic focus of a sibling —
   * is never hijacked.
   */
  const handleFocus = useCallback(e => {
    // Only correct the no-selection case; a selected value already provides
    // a deterministic native tab stop.
    if (value !== '') {
      return;
    }
    const group = groupRef.current;
    if (!group) {
      return;
    }

    // Ignore focus moving *within* the group. `relatedTarget` is the element
    // losing focus; if it was inside the group, this is intra-group movement
    // (arrow keys, clicking a sibling) and must not be hijacked. Some browsers
    // report a null `relatedTarget`; fall back to checking whether the group
    // already contained the active element before this focus landed.
    const relatedTarget = e.relatedTarget;
    if (relatedTarget) {
      if (group.contains(relatedTarget)) {
        return;
      }
    } else if (document.activeElement && document.activeElement !== e.target && group.contains(document.activeElement)) {
      return;
    }
    const radios = Array.from(group.querySelectorAll('input[type="radio"]:not([disabled])'));
    if (radios.length === 0) {
      return;
    }
    const target = e.target;
    const targetIndex = radios.findIndex(radio => radio === target);
    // Only correct when the focus target is one of our enabled radios (not a
    // nested control such as end-content).
    if (targetIndex === -1) {
      return;
    }

    // Infer tab direction from which end the browser chose. On a backward
    // (Shift+Tab) entry into an unselected group, browsers focus the *last*
    // radio; keep it as the deterministic backward tab stop. Any other entry
    // (forward Tab, or a browser that lands mid-group) is normalized to the
    // *first* enabled radio, the APG default.
    const isBackwardEntry = targetIndex === radios.length - 1;
    const intended = isBackwardEntry ? radios[radios.length - 1] : radios[0];
    if (target !== intended) {
      intended.focus();
    }
  }, [value]);
  return /*#__PURE__*/_jsxs(Field, {
    ref: ref,
    "data-testid": dataTestId,
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: inputID,
    labelID: labelID,
    isGroupLabel: true,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    statusVariant: "detached",
    width: width,
    xstyle: xstyle,
    className: className,
    style: style,
    children: [/*#__PURE__*/_jsx("div", {
      ref: el => {
        groupRef.current = el;
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, so attaching
        // unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      role: "radiogroup",
      "aria-labelledby": labelID,
      onFocus: handleFocus,
      "aria-describedby": [description ? descriptionID : null, status?.message ? statusMessageID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined,
      "aria-invalid": status?.type === 'error' ? true : undefined,
      "aria-required": isRequired || undefined,
      ...mergeProps(themeProps('radio-list', {
        orientation,
        size
      }), {
        0: {
          className: "x78zum5 x1q0g3np x9mgr7n"
        },
        1: {
          className: "x78zum5 xdt5ytf x1txdalj"
        }
      }[!!(orientation === 'vertical') << 0]),
      children: /*#__PURE__*/_jsx(RadioListContext, {
        value: contextValue,
        children: children
      })
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
RadioList.displayName = 'RadioList';